import { Controller } from "@hotwired/stimulus"

/**
 * Inline Address Form Controller
 *
 * Handles AJAX form submission for billing addresses within a drawer.
 * On success, dispatches events to update the parent address selector.
 *
 * NOTE: This controller uses a <div> instead of <form> to avoid nested form
 * issues when rendered inside another form (like the checkout page).
 */
export default class extends Controller {
  static targets = ["errors", "errorMessages", "submit", "field"]
  static values = {
    url: String,
    method: String,
    isNew: Boolean
  }

  handleSubmit(event) {
    if (event) event.preventDefault()

    // Disable submit button and show loading state
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = true
      this.originalButtonText = this.submitTarget.textContent
      this.submitTarget.textContent = "Salvando..."
    }

    // Hide previous errors
    if (this.hasErrorsTarget) {
      this.errorsTarget.classList.add("hidden")
    }

    // Build FormData from field targets
    const formData = new FormData()

    // Collect all field values
    this.fieldTargets.forEach(field => {
      if (field.name) {
        formData.append(field.name, field.value)
      }
    })

    // Get CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    const url = this.urlValue
    const method = this.methodValue || "POST"

    // Submit via fetch with JSON Accept header
    fetch(url, {
      method: method,
      body: formData,
      headers: {
        "Accept": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": csrfToken
      },
      credentials: "same-origin"
    })
    .then(response => {
      if (response.ok) {
        return response.json().then(data => this.handleSuccess(data))
      } else {
        return response.json().then(data => this.handleError(data))
      }
    })
    .catch(error => {
      console.error("Error submitting address form:", error)
      this.handleError({ errors: ["Ocorreu um erro ao salvar. Tente novamente."] })
    })
  }

  handleSuccess(data) {
    // Dispatch custom event with the new/updated address data
    const customEvent = new CustomEvent("billing-address:saved", {
      bubbles: true,
      detail: {
        address: data.address,
        isNew: data.is_new
      }
    })
    this.element.dispatchEvent(customEvent)

    // Close the drawer
    const drawer = this.element.closest("[data-controller='slide-drawer']")
    if (drawer) {
      const drawerController = this.application.getControllerForElementAndIdentifier(drawer, "slide-drawer")
      if (drawerController) {
        drawerController.close()
      }
    }

    // Show success toast/notification (if available)
    this.showNotification(data.message || "Endereço salvo com sucesso!", "success")
  }

  handleError(data) {
    // Re-enable submit button
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = false
      this.submitTarget.textContent = this.originalButtonText || (this.isNewValue ? "Adicionar" : "Salvar")
    }

    if (data.errors && this.hasErrorsTarget && this.hasErrorMessagesTarget) {
      // Show validation errors
      const errorList = Array.isArray(data.errors) ? data.errors : Object.values(data.errors).flat()
      this.errorMessagesTarget.innerHTML = errorList.map(e => `<p>${e}</p>`).join("")
      this.errorsTarget.classList.remove("hidden")

      // Scroll to errors
      this.errorsTarget.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  showNotification(message, type = "success") {
    // Check if there's a notification system available
    const event = new CustomEvent("notification:show", {
      bubbles: true,
      detail: { message, type }
    })
    document.dispatchEvent(event)
  }
}
